import React, { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel  from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import NorthIcon from '@mui/icons-material/North';

import { LineChart, Tooltip, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import trajectory from '../lib/trajectory.ts'
import toFixed from '../lib/toFixed'

function BallisticCalculator() {
  const [ zeroRange, setZeroRange ] = useAutoSaveState('zeroRange', '100')
  const [ latitude, setLatitude ] = useAutoSaveState('latitude', '')
  const [ azimuth, setAzimuth ] = useAutoSaveState('azimuth', '')
  const [ bc, setBc ] = useAutoSaveState('bc', '')
  const [ bulletDiameter, setBulletDiameter ] = useAutoSaveState('bulletDiameter', '')
  const [ bulletLength, setBulletLength ] = useAutoSaveState('bulletLength', '')
  const [ bulletWeight, setBulletWeight ] = useAutoSaveState('bulletWeight', '')
  const [ dragCurve, setDragCurve ] = useAutoSaveState('dragCurve', 'G7')
  const [ humidity, setHumidity ] = useAutoSaveState('humidity', '50')
  const [ lookAngle, setLookAngle ] = useAutoSaveState('lookAngle', '0')
  const [ pressure, setPressure ] = useAutoSaveState('pressure', '29.92')
  const [ range, setRange ] = useAutoSaveState('range', '1000')
  const [ rangeStep, setRangeStep ] = useAutoSaveState('rangeStep', '100')
  const [ scopeHeight, setScopeHeight ] = useAutoSaveState('scopeHeight', '1.5')
  const [ temperature, setTemperature ] = useAutoSaveState('temperature', '59')
  const [ twistDirection, setTwistDirection ] = useAutoSaveState('twistDirection', '1')
  const [ twistRate, setTwistRate ] = useAutoSaveState('twistRate', '')
  const [ useAerodynamicJump, setUseAerodynamicJump ] = useAutoSaveState('useAerodynamicJump', true)
  const [ useSpinDrift, setUseSpinDrift ] = useAutoSaveState('useSpinDrift', true)
  const [ v0, setV0 ] = useAutoSaveState('v0', '')
  const [ wind, setWind ] = useAutoSaveState('wind', [ { speed: '10', direction: '90' } ])
  const [ outputUnit, setOutputUnit ] = useAutoSaveState('outputUnit', 'MOA')
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [ result, setResult ] = useState(null)
  const [ error, setError ] = useState(false)

  const addWind = () => {
    const r1 = parseInt(range)
    const r2 = parseInt(wind[wind.length - 1].range) || 0
    const newRange = Math.round(((r1 - r2) / 2) + r2).toString()
    setWind([ ...wind, { speed: '10', direction: '90', range: newRange } ])
  }

  const removeWind = () => {
    if (wind.length > 1) {
      const newWind = [ ...wind ]
      newWind.pop()
      setWind(newWind)
    }
  }

  const calculate = () => {
    let requiredMissing = false
    if (!zeroRange) requiredMissing = true
    if (!bc) requiredMissing = true
    if (!pressure) requiredMissing = true
    if (!range) requiredMissing = true
    if (!scopeHeight) requiredMissing = true
    if (!temperature) requiredMissing = true
    if (!v0) requiredMissing = true
    if (requiredMissing) {
      setError(true)
      return
    } else {
      setError(false)
    }
    const options = {
      zeroRange,
      latitude,
      azimuth,
      bc,
      bulletDiameter,
      bulletLength,
      bulletWeight,
      dragCurve,
      humidity,
      lookAngle,
      pressure,
      range,
      rangeStep,
      scopeHeight,
      temperature,
      twistDirection,
      twistRate,
      useAerodynamicJump,
      useSpinDrift,
      v0,
      wind,
      outputUnit,
    }
    console.log({ options })
    const result = trajectory(options)
    console.log({ result })
    setResult(result)
    setPreview(`${v0}fps, ${bc}BC, ${range}yds`)
    window.gtag('event', 'calculate-ballistic-calculator')
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} sx={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Ballistic Calculator
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid xs={12} container mt={2} justifyContent="center">
                  <Typography variant="h5" gutterBottom>
                    Ammo
                  </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField error={error && !v0} required fullWidth label="Muzzle Velocity" value={v0} onChange={e => setV0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">fps</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField error={error && !bc} required fullWidth label="BC" value={bc} onChange={e => setBc(e.target.value)}  />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Drag Model</InputLabel>
                  <Select
                    value={dragCurve}
                    label="Drag Curve"
                    onChange={e => setDragCurve(e.target.value)}
                  >
                    <MenuItem value="G7">G7</MenuItem>
                    <MenuItem value="G1">G1</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Caliber" value={bulletDiameter} onChange={e => setBulletDiameter(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Bullet Weight" value={bulletWeight} onChange={e => setBulletWeight(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">gr</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Bullet Length" value={bulletLength} onChange={e => setBulletLength(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid xs={12} container mt={2} justifyContent="center">
                <Typography variant="h5" gutterBottom>
                  Rifle
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField error={error && !zeroRange} required fullWidth label="Zero Range" value={zeroRange} onChange={e => setZeroRange(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">yards</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField error={error && !scopeHeight} required fullWidth label="Scope Height" value={scopeHeight} onChange={e => setScopeHeight(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Twist Rate" value={twistRate} onChange={e => setTwistRate(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in/rev</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Twist Direction</InputLabel>
                  <Select
                    value={twistDirection}
                    label="Twist Direction"
                    onChange={e => setTwistDirection(e.target.value)}
                  >
                    <MenuItem value="1">Right</MenuItem>
                    <MenuItem value="-1">Left</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid xs={12} container mt={2} justifyContent="center">
                <Typography variant="h5" gutterBottom>
                  Environment
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField error={error && !temperature} required fullWidth label="Temperature" value={temperature} onChange={e => setTemperature(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">°F</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField error={error && !pressure} required fullWidth label="Pressure" value={pressure} onChange={e => setPressure(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">inHg</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Humidity" value={humidity} onChange={e => setHumidity(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Look Angle" value={lookAngle} onChange={e => setLookAngle(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Latitude" value={latitude} onChange={e => setLatitude(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Azimuth" value={azimuth} onChange={e => setAzimuth(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid xs={12} container mt={2} justifyContent="center">
                <Typography variant="h5" gutterBottom>
                  Wind
                </Typography>
              </Grid>
              {wind.map((crosswind, i) => (<>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Wind Speed"
                    value={crosswind.speed}
                    onChange={e => {
                      const newWind = [ ...wind ]
                      newWind[i] = {
                        ...wind[i],
                        speed: e.target.value,
                      }
                      setWind(newWind)
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">mph</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Wind Direction"
                    value={crosswind.direction}
                    onChange={e => {
                      const newWind = [ ...wind ]
                      newWind[i] = {
                        ...wind[i],
                        direction: e.target.value
                      }
                      setWind(newWind)
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end"><NorthIcon style={{ transform: `rotate(${crosswind.direction}deg)` }} /></InputAdornment> }}
                  />
                </Grid>
                {i === 0 && <>
                  <Grid item xs={1} />
                  <Grid item xs={wind.length > 1 ? 1 : 2}>
                    <Grid container justifyContent="center">
                      <Button variant="contained" onClick={addWind}>+</Button>
                    </Grid>
                  </Grid>
                  {wind.length > 1 && <Grid item xs={1}>
                    <Button variant="contained" onClick={removeWind}>-</Button>
                  </Grid>}
                  <Grid item xs={1} />
                </>}
                {i > 0 && <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Start Range"
                      value={crosswind.range}
                      onChange={e => {
                        const newWind = [ ...wind ]
                        newWind[i] = {
                          ...wind[i],
                          range: e.target.value
                        }
                        setWind(newWind)
                      }}
                      InputProps={{ endAdornment: <InputAdornment position="end">yards</InputAdornment> }}
                    />
                  </Grid>
                </>}
              </>))}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid xs={12} container mt={2} justifyContent="center">
                <Typography variant="h5" gutterBottom>
                  Settings
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField error={error && !range} required fullWidth label="Target Range" value={range} onChange={e => setRange(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">yards</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Range Step" value={rangeStep} onChange={e => setRangeStep(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">yards</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Output Unit</InputLabel>
                  <Select
                    value={outputUnit}
                    label="Output Unit"
                    onChange={e => setOutputUnit(e.target.value)}
                  >
                    <MenuItem value="MOA">MOA</MenuItem>
                    <MenuItem value="Mils">Mils</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useAerodynamicJump}
                      onChange={e => setUseAerodynamicJump(e.target.checked)}
                    />}
                  label="Use Aerodynamic Jump"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useSpinDrift}
                      onChange={e => setUseSpinDrift(e.target.checked)}
                    />}
                  label="Use Spin Drift"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
            {error && <Grid container xs={12} justifyContent="center" mt={2}>
              <Alert severity="error">Required fields are missing</Alert>
            </Grid>}
          </Paper>
        </Grid>
        {result && <>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Grid container xs={12} justifyContent="center">
                <Typography variant="h5" gutterBottom>
                  <GpsFixedIcon /> Trajectory
                </Typography>
              </Grid>
              <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell component="th">Range</TableCell>
                      <TableCell component="th">Drop Inches</TableCell>
                      <TableCell component="th">Drop {outputUnit}</TableCell>
                      <TableCell component="th">Wind Inches</TableCell>
                      <TableCell component="th">Wind {outputUnit}</TableCell>
                      <TableCell component="th">Velocity</TableCell>
                      <TableCell component="th">Horizontal Velocity</TableCell>
                      {bulletWeight > 0 && <TableCell component="th">Energy</TableCell>}
                      <TableCell component="th">TOF</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.values(result).map(row => (
                      <TableRow
                        key={row.name}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>
                          {row.range}
                        </TableCell>
                        <TableCell>
                          {toFixed(row.drop, 1)}
                        </TableCell>
                        <TableCell>
                          {toFixed(outputUnit === 'Mils' ? Math.abs(row.dropMils) : Math.abs(row.dropMOA), 1)}
                          {row.dropMOA > 0 ? ' D' : ' U'}
                        </TableCell>
                        <TableCell>{toFixed(row.wind, 1)}</TableCell>
                        <TableCell>
                          {toFixed(outputUnit === 'Mils' ? Math.abs(row.windMils) : Math.abs(row.windMOA), 1)}
                          {row.windMOA > 0 ? ' L' : ' R'}
                        </TableCell>
                        <TableCell>{toFixed(row.velocity, 0)}</TableCell>
                        <TableCell>{toFixed(row.windvelocity, 0)}</TableCell>
                        {bulletWeight > 0 && <TableCell>{toFixed(row.energy, 0)}</TableCell>}
                        <TableCell>{toFixed(row.time, 3)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }} elevation={6}>
              <Typography variant="h5" gutterBottom style={{ textAlign: 'center' }}>Drop</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={Object.values(result).filter(v => v.range > 0).map(v => ({ ...v, range: parseInt(v.range) }))} >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" label={{ value: 'Yards', position: 'insideBottom', offset: -5 }} />
                  <YAxis />
                  <Tooltip formatter={(value) => Math.abs(value).toFixed(2) + (value > 0 ? 'in U' : 'in D')} />
                  <Line type="number" dataKey="drop" dot={false} stroke="#FF6384" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }} elevation={6}>
              <Typography variant="h5" gutterBottom style={{ textAlign: 'center' }}>Wind Drift</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={Object.values(result).filter(v => v.range > 0).map(v => ({ ...v, range: parseInt(v.range) }))} >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" label={{ value: 'Yards', position: 'insideBottom', offset: -5 }} />
                  <YAxis type="number" />
                  <Tooltip formatter={(value) => Math.abs(value).toFixed(2) + (value > 0 ? 'in L' : 'in R')} />
                  <Line type="number" dataKey="wind" dot={false} stroke="#FF6384" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </>}
      </Grid>
    </Container>
  );
}

export default function WrappedBallisticCalculator() {
  return (
    <AutoSaveProvider>
      <BallisticCalculator />
    </AutoSaveProvider>
  )
}
